import { useCallback, useEffect, useRef, useState } from 'react';
import { confirmSessionIntake, getSessionIntake, type IntakeField } from '@/api/intake';
import { useT } from '@/i18n/runtime';
import { formatIntakeValue } from '@/api/intakeDisplay';
import { useSessionStore } from '@/store/sessionStore';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Button } from '../components/Button';

/**
 * SCREEN · CONFIRM YOUR DETAILS — the SPEC-14 §5 golden-source intake confirm.
 *
 * The pre-onboarding record the CLIENT supplied is the golden source this
 * verification is measured against; the customer sees it read-only
 * (IMMUTABLE_DISPLAY — tamper-proofing is the point), fills any blanks the
 * template opened to them (CUSTOMER_COMPLETABLE, editable ONCE — a blank they
 * fill here, never a golden value they can overwrite), and confirms. The
 * confirm is a chained consent-class event RECORDED BY THE BE — this screen
 * only reports it.
 *
 * Honest absence (fail-soft by directive): a session with NO intake — none
 * configured, or an older be without the route — skips this step ENTIRELY:
 * the loading beat auto-advances and the customer never sees an empty
 * ceremony. HIDDEN fields are filtered server-side and never reach this
 * client, so there is nothing here to "not render".
 */
export function ConfirmDetails({ tenant }: StepProps): JSX.Element {
  const { t, locale } = useT();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);

  type Phase = 'loading' | 'form' | 'submitting' | 'error';
  const [phase, setPhase] = useState<Phase>('loading');
  const [fields, setFields] = useState<readonly IntakeField[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const advanced = useRef(false);

  const skip = useCallback(() => {
    if (advanced.current) return;
    advanced.current = true;
    advance();
  }, [advance]);

  useEffect(() => {
    let active = true;
    getSessionIntake(sessionId)
      .then((served) => {
        if (!active) return;
        if (!served) {
          skip(); // honest absence — no intake, no ceremony
          return;
        }
        setFields(served);
        setPhase('form');
      })
      .catch(() => {
        // getSessionIntake never throws by contract; belt-and-braces skip.
        if (active) skip();
      });
    return () => {
      active = false;
    };
  }, [sessionId, skip]);

  // The CUSTOMER_COMPLETABLE blanks — the only rows that render an input.
  // A completable field that already CARRIES a value was filled before
  // (editable once) and renders read-only like the golden rows.
  const editable = fields.filter(
    (f) => f.mode === 'CUSTOMER_COMPLETABLE' && (f.value === null || f.value === ''),
  );

  const submit = async (): Promise<void> => {
    if (phase === 'submitting') return;
    setPhase('submitting');
    try {
      const values: Record<string, string> = {};
      for (const f of editable) {
        const v = (drafts[f.key] ?? '').trim();
        if (v !== '') values[f.key] = v;
      }
      await confirmSessionIntake(sessionId, values);
      skip(); // the be recorded the chained confirm — continue
    } catch {
      setPhase('error');
    }
  };

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.confirm.subtitle')} />
      <main className="center-col" style={{ padding: 16 }} aria-label={t('droom.confirm.aria')}>
        {phase === 'loading' ? (
          <p className="sub" role="status">
            {t('droom.confirm.loading')}
          </p>
        ) : (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.confirm.title')}</h2>
            <p className="sub">{t('droom.confirm.sub')}</p>

            {/* Item 14 (owner, 2026-08-24) — a DEFINITION LIST, not a centred stack.
                The customer is being asked whether details someone else recorded about them are
                correct, and the old card centred every label and value in one column with no
                hierarchy: four bold values floating in the middle of the card, a raw ISO date among
                them. Left-aligned rows with a quiet label above a prominent value is how a person
                scans their own record; the values are also FORMATTED for reading (see
                api/intakeDisplay) without ever being rewritten. */}
            <dl className="confirm-list">
              {fields.map((f) => {
                const isBlank = editable.some((e) => e.key === f.key);
                return (
                  <div className="confirm-row" key={f.key}>
                    <dt className="confirm-label">{f.label}</dt>
                    <dd className="confirm-value">
                      {isBlank ? (
                        <input
                          className="field-input"
                          type="text"
                          value={drafts[f.key] ?? ''}
                          onChange={(e) =>
                            setDrafts((d) => ({ ...d, [f.key]: e.target.value }))
                          }
                          aria-label={f.label}
                        />
                      ) : f.value !== null && f.value !== '' ? (
                        /* Read-only by design (IMMUTABLE_DISPLAY): the golden value is the
                           reference this verification is measured against. */
                        formatIntakeValue(f.key, f.value, locale)
                      ) : (
                        <span className="confirm-value--absent">{t('droom.confirm.notProvided')}</span>
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>

            <p className="sub" style={{ fontSize: 12 }}>
              {t('droom.confirm.readonlyNote')}
            </p>

            {phase === 'error' ? (
              <p className="field-error" role="alert">
                {t('droom.confirm.err')}
              </p>
            ) : null}

            <Button disabled={phase === 'submitting'} onClick={() => void submit()}>
              {phase === 'submitting'
                ? t('droom.confirm.submitting')
                : t('droom.confirm.cta')}
            </Button>
          </section>
        )}
      </main>
    </div>
  );
}
